import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FiCheck,
  FiX,
  FiEdit3,
  FiTrash2,
  FiRefreshCw,
  FiClock,
  FiImage,
  FiSend,
} from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * ReviewQueuePanel - Modal hiển thị danh sách bài viết chờ duyệt
 *
 * Props:
 * - userId: string - ID người dùng
 * - configId: string - ID cấu hình đăng bài
 * - isVisible: boolean - Cho phép hiển thị trigger button
 */
const ReviewQueuePanel = ({ userId, configId, isVisible, basePath = "posts" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editImages, setEditImages] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  // Fetch review queue items
  const fetchReviewQueue = useCallback(async () => {
    if (!userId || !configId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/review-queue/${configId}`,
        { params: { userId, basePath } }
      );
      if (response.data.success) {
        setItems(response.data.data || []);
      }
    } catch (error) {
      console.error("Lỗi khi tải review queue:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, configId, basePath]);

  useEffect(() => {
    if (isVisible) {
      fetchReviewQueue();
    }
  }, [isVisible, fetchReviewQueue]);

  // Edit functions
  const startEditing = (item) => {
    setEditingId(item.id);
    setEditContent(item.editedContent || item.content);
    setEditImages(item.editedImages || item.images || []);
  };

  const saveEdit = async (reviewId) => {
    setProcessingId(reviewId);
    try {
      await axios.put(
        `${API_BASE_URL}/api/review-queue/${configId}/${reviewId}`,
        {
          userId,
          content: editContent,
          images: editImages,
          basePath,
        }
      );
      toast.success("Đã lưu chỉnh sửa thành công!");
      setEditingId(null);
      await fetchReviewQueue();
    } catch (error) {
      toast.error(error.response?.data?.message || "Lỗi khi lưu chỉnh sửa");
    } finally {
      setProcessingId(null);
    }
  };

  // Approve / Reject / Delete handlers
  const handleApprove = (item) => {
    setConfirmAction({ type: "approve", item });
  };

  const executeApprove = async () => {
    const item = confirmAction?.item;
    if (!item) return;
    setConfirmAction(null);
    setProcessingId(item.id);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/review-queue/${configId}/${item.id}/approve`,
        { userId, basePath }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      await fetchReviewQueue();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Lỗi khi duyệt bài viết"
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = (item) => {
    setConfirmAction({ type: "reject", item });
  };

  const executeReject = async () => {
    const item = confirmAction?.item;
    if (!item) return;
    setConfirmAction(null);
    setProcessingId(item.id);
    try {
      await axios.post(
        `${API_BASE_URL}/api/review-queue/${configId}/${item.id}/reject`,
        { userId, basePath }
      );
      toast.info("Đã từ chối bài viết");
      await fetchReviewQueue();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Lỗi khi từ chối bài viết"
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = (item) => {
    setConfirmAction({ type: "delete", item });
  };

  const executeDelete = async () => {
    const item = confirmAction?.item;
    if (!item) return;
    setConfirmAction(null);
    setProcessingId(item.id);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/review-queue/${configId}/${item.id}`,
        { params: { userId, basePath } }
      );
      toast.success("Đã xoá bài viết khỏi hàng đợi");
      await fetchReviewQueue();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Lỗi khi xoá bài viết"
      );
    } finally {
      setProcessingId(null);
    }
  };

  // Confirm modal config
  const confirmConfig = {
    approve: {
      icon: <FiSend className="h-6 w-6 text-green-600" />,
      title: "Xác nhận duyệt bài",
      message: `Bạn có chắc muốn duyệt và đăng bài lên Facebook?`,
      detail: "Bài viết sẽ được đăng ngay lên các fanpage đã cấu hình.",
      confirmLabel: "Duyệt & Đăng",
      confirmClass: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
      onConfirm: () => executeApprove(),
    },
    reject: {
      icon: <FiX className="h-6 w-6 text-red-500" />,
      title: "Xác nhận từ chối",
      message: `Bạn có chắc muốn từ chối bài viết này?`,
      detail: "Chủ đề sẽ được tạo lại trong lần chạy tiếp theo.",
      confirmLabel: "Từ chối",
      confirmClass: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      onConfirm: () => executeReject(),
    },
    delete: {
      icon: <FiTrash2 className="h-6 w-6 text-red-600" />,
      title: "Xác nhận xoá",
      message: `Bạn có chắc muốn xoá bài viết khỏi hàng đợi duyệt?`,
      detail: "Hành động này không thể hoàn tác.",
      confirmLabel: "Xoá",
      confirmClass: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      onConfirm: () => executeDelete(),
    },
  };

  // Image edit handlers
  const handleAddImage = () => {
    setEditImages([...editImages, ""]);
  };

  const handleRemoveImage = (index) => {
    setEditImages(editImages.filter((_, i) => i !== index));
  };

  const handleImageUrlChange = (index, value) => {
    const updated = [...editImages];
    updated[index] = value;
    setEditImages(updated);
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Vừa xong";
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString("vi-VN");
  };

  if (!isVisible) return null;

  const pendingItems = items.filter((i) => i.status === "pending");
  const filteredItems =
    filter === "all"
      ? items
      : items.filter((i) => i.status === filter);

  const filterTabs = [
    { key: "all", label: "Tất cả", count: items.length },
    { key: "pending", label: "Chờ duyệt", count: items.filter((i) => i.status === "pending").length },
    { key: "published", label: "Đã đăng", count: items.filter((i) => i.status === "published").length },
    { key: "rejected", label: "Đã từ chối", count: items.filter((i) => i.status === "rejected").length },
    { key: "failed", label: "Thất bại", count: items.filter((i) => i.status === "failed").length },
  ].filter((t) => t.count > 0);

  return (
    <>
      {/* Floating trigger button */}
      <div className="flex justify-center my-3">
        <button
          onClick={() => {
            setIsOpen(true);
            fetchReviewQueue();
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md hover:border-amber-300 transition-all duration-200 text-sm font-medium text-gray-700 hover:text-amber-600 group"
        >
          <svg
            className="w-4 h-4 text-amber-500 group-hover:text-amber-600 transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          Hàng đợi duyệt bài
          {pendingItems.length > 0 && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-amber-500 text-white text-xs font-bold rounded-full">
              {pendingItems.length}
            </span>
          )}
        </button>
      </div>

      {/* Modal overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative w-full sm:max-w-2xl h-[85vh] bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: "reviewSlideUp 0.25s ease-out",
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shadow-sm">
                  <FiClock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-800">
                    Hàng đợi duyệt bài
                  </h2>
                  <p className="text-xs text-gray-500">
                    {items.length} bài viết
                    {pendingItems.length > 0 &&
                      ` · ${pendingItems.length} chờ duyệt`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchReviewQueue}
                  disabled={loading}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Tải lại"
                >
                  <FiRefreshCw className={loading ? "animate-spin" : ""} size={16} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-1 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                    filter === tab.key
                      ? "bg-amber-500 text-white shadow-sm"
                      : "text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Content list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
              {loading && items.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <FiRefreshCw className="animate-spin mx-auto mb-3 w-8 h-8 text-gray-300" />
                  <p className="text-sm">Đang tải...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="text-sm">Không có bài viết nào</p>
                  <p className="text-xs mt-1">
                    Bài viết sẽ xuất hiện khi hệ thống tạo nội dung theo lịch
                  </p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <ReviewCard
                    key={item.id}
                    item={item}
                    editingId={editingId}
                    editContent={editContent}
                    editImages={editImages}
                    processingId={processingId}
                    formatTime={formatTime}
                    onStartEdit={startEditing}
                    onSaveEdit={saveEdit}
                    onCancelEdit={() => setEditingId(null)}
                    onSetEditContent={setEditContent}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onDelete={handleDelete}
                    onAddImage={handleAddImage}
                    onRemoveImage={handleRemoveImage}
                    onImageUrlChange={handleImageUrlChange}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmAction && confirmConfig[confirmAction.type] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4"
            style={{ animation: "reviewSlideUp 0.2s ease-out" }}
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  {confirmConfig[confirmAction.type].icon}
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {confirmConfig[confirmAction.type].title}
                  </h3>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  {confirmConfig[confirmAction.type].message}
                </p>
                {confirmAction.item?.topicTitle && (
                  <p className="text-sm font-medium text-gray-700 mt-2 p-2 bg-gray-50 rounded-lg">
                    "{confirmAction.item.topicTitle}"
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  {confirmConfig[confirmAction.type].detail}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Huỷ
                </button>
                <button
                  onClick={confirmConfig[confirmAction.type].onConfirm}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmConfig[confirmAction.type].confirmClass}`}
                >
                  {confirmConfig[confirmAction.type].confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation keyframe */}
      <style>{`
        @keyframes reviewSlideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

// Extracted card component
const ReviewCard = ({
  item,
  editingId,
  editContent,
  editImages,
  processingId,
  formatTime,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onSetEditContent,
  onApprove,
  onReject,
  onDelete,
  onAddImage,
  onRemoveImage,
  onImageUrlChange,
}) => {
  const [showFull, setShowFull] = useState(false);
  const isEditing = editingId === item.id;
  const isProcessing = processingId === item.id;

  const statusMap = {
    pending: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      badge: "bg-amber-100 text-amber-700",
      dot: "bg-amber-400",
      label: "Chờ duyệt",
    },
    published: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      badge: "bg-emerald-100 text-emerald-700",
      dot: "bg-emerald-400",
      label: "Đã đăng",
    },
    rejected: {
      bg: "bg-red-50",
      border: "border-red-200",
      badge: "bg-red-100 text-red-700",
      dot: "bg-red-400",
      label: "Đã từ chối",
    },
    failed: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      badge: "bg-gray-100 text-gray-700",
      dot: "bg-gray-400",
      label: "Thất bại",
    },
  };

  const s = statusMap[item.status] || statusMap.pending;
  const content = item.editedContent || item.content || "";
  const images = item.editedImages || item.images || [];
  const isLong = content.length > 300;

  return (
    <div
      className={`rounded-xl border ${s.border} ${s.bg} p-3.5 transition-all hover:shadow-sm`}
    >
      {/* Top row: title + status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${s.badge}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {s.label}
          </span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500">
            {formatTime(item.createdAt)}
          </span>
        </div>
      </div>

      {/* Topic title */}
      {item.topicTitle && (
        <p className="text-sm font-medium text-gray-700 mb-2">
          {item.topicTitle}
        </p>
      )}

      {isEditing ? (
        /* Edit mode */
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Nội dung bài viết
            </label>
            <textarea
              value={editContent}
              onChange={(e) => onSetEditContent(e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-lg text-sm resize-vertical focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
              rows={8}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FiImage className="inline mr-1" size={12} />
              Hình ảnh ({editImages.length})
            </label>
            {editImages.map((url, index) => (
              <div
                key={index}
                className="flex items-center gap-2 mb-2"
              >
                <input
                  type="text"
                  value={url}
                  onChange={(e) =>
                    onImageUrlChange(index, e.target.value)
                  }
                  placeholder="URL hình ảnh..."
                  className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                />
                {url && (
                  <img
                    src={url}
                    alt="preview"
                    className="w-10 h-10 object-cover rounded-lg border"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <button
                  onClick={() => onRemoveImage(index)}
                  className="p-1 text-red-400 hover:text-red-600"
                >
                  <FiX size={16} />
                </button>
              </div>
            ))}
            <button
              onClick={onAddImage}
              className="text-xs text-amber-600 hover:text-amber-800 font-medium"
            >
              + Thêm hình ảnh
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onSaveEdit(item.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
            >
              {isProcessing ? (
                <FiRefreshCw className="animate-spin" size={14} />
              ) : (
                <FiCheck size={14} />
              )}
              Lưu
            </button>
            <button
              onClick={onCancelEdit}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              <FiX size={14} /> Huỷ
            </button>
          </div>
        </div>
      ) : (
        /* View mode */
        <div>
          {/* Content */}
          <div className="mb-2">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {showFull
                ? content
                : content.substring(0, 300) + (isLong ? "..." : "")}
            </p>
            {isLong && (
              <button
                onClick={() => setShowFull(!showFull)}
                className="text-xs text-amber-500 hover:text-amber-600 mt-1 font-medium"
              >
                {showFull ? "Thu gọn" : "Xem thêm"}
              </button>
            )}
          </div>

          {/* Image thumbnails */}
          {images.length > 0 && (
            <div className="flex gap-1.5 mb-2 overflow-x-auto">
              {images.slice(0, 4).map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Ảnh ${i + 1}`}
                  className="w-12 h-12 rounded-lg object-cover border border-white shadow-sm flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ))}
              {images.length > 4 && (
                <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-xs text-gray-500 font-medium flex-shrink-0">
                  +{images.length - 4}
                </div>
              )}
            </div>
          )}

          {/* Action buttons - only for pending */}
          {item.status === "pending" && (
            <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-gray-200/60">
              <button
                onClick={() => onApprove(item)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {isProcessing ? (
                  <FiRefreshCw className="animate-spin" size={13} />
                ) : (
                  <FiSend size={13} />
                )}
                Duyệt & Đăng
              </button>
              <button
                onClick={() => onStartEdit(item)}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-amber-700 text-xs font-medium rounded-lg border border-amber-200 hover:bg-amber-50 transition-colors"
              >
                <FiEdit3 size={13} /> Sửa
              </button>
              <button
                onClick={() => onReject(item)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-red-600 text-xs font-medium rounded-lg border border-red-200 hover:bg-red-50 transition-colors"
              >
                <FiX size={13} /> Từ chối
              </button>
              <button
                onClick={() => onDelete(item)}
                disabled={isProcessing}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-gray-500 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <FiTrash2 size={13} /> Xoá
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewQueuePanel;
